import { err, ok } from "neverthrow";
import z from "zod";
import { dayjs } from "../dayjs/dayjs.js";
import { buildInvalidError } from "../neverthrow/error.js";
import { ISO_DATE_FORMAT, IsoDateSchema } from "./date.js";
import { END_OF_DAY, TimeOfDaySchema } from "./time.js";
export const InstantRangeSchema = z.object({
    start: z.iso.datetime(),
    end: z.iso.datetime(),
});
/**
 * The default zone for reading a wall-clock time. A clock reading carries no
 * offset, so turning one into an instant — or asking what today is — needs a
 * zone, and callers that must pin a different one pass it.
 */
export const UK_TIME_ZONE = "Europe/London";
const TIME_FORMAT = "HH:mm:ss";
/**
 * The instant at which clocks in `timeZone` show `wallClock`, a
 * `YYYY-MM-DD HH:mm:ss` reading. A reading they show twice, in the hour they go
 * back, is its first occurrence; one they never show, in the hour they go
 * forward, is moved later by the length of the jump. These are the
 * "compatible" choices of `Temporal` and `java.time`.
 *
 * Use this rather than `dayjs.tz` given a string, which starts from today's
 * offset and so reads the repeated hour differently in summer and winter.
 */
export function instantOfWallClock({ wallClock, timeZone = UK_TIME_ZONE, }) {
    const readAsUtc = dayjs.utc(wallClock);
    const offsetAt = (instant) => instant.tz(timeZone).utcOffset();
    // A day either side of the reading straddles at most one transition, so the
    // offsets there are the only two the reading can mean.
    const offsetBefore = offsetAt(readAsUtc.subtract(1, "day"));
    const offsetAfter = offsetAt(readAsUtc.add(1, "day"));
    const atOffsetBefore = readAsUtc.subtract(offsetBefore, "minute");
    const atOffsetAfter = readAsUtc.subtract(offsetAfter, "minute");
    // Read with the offset before, a repeated time is its first occurrence and a
    // skipped one lands past the jump. Only a time the clocks show within a day
    // after a transition needs the offset after.
    return offsetAt(atOffsetBefore) === offsetBefore ||
        offsetAt(atOffsetAfter) !== offsetAfter
        ? inTimeZone(atOffsetBefore, offsetBefore, timeZone)
        : inTimeZone(atOffsetAfter, offsetAfter, timeZone);
}
/**
 * `instant` at `offset`, marked as in `timeZone` as `dayjs.tz` marks its own
 * result, since code such as MUI's date pickers reads the zone off the value.
 * Not `Dayjs#tz`: it rebuilds the value from its wall clock read in the host's
 * zone, which moves a time that falls in the host's own skipped hour.
 */
function inTimeZone(instant, offset, timeZone) {
    const zoned = dayjs(instant.valueOf()).utcOffset(offset);
    const internals = Reflect.get(zoned, "$x");
    // Stryker disable all -- every dayjs value has $x, so the guard only fails
    // after a dayjs upgrade, which is what it exists to report.
    /* v8 ignore next 3 -- unreachable with the dayjs this package pins */
    if (typeof internals !== "object" || internals === null) {
        throw new Error("dayjs no longer keeps a value's time zone in $x");
    }
    // Stryker restore all
    Reflect.set(internals, "$timezone", timeZone);
    return zoned;
}
/** The date UK clocks show at `instant`. */
export const ukDateOf = (instant) => IsoDateSchema.parse(dayjs(instant).tz(UK_TIME_ZONE).format(ISO_DATE_FORMAT));
/**
 * A wall-clock span on a date as the instants it actually occupies, or
 * `undefined` when it names time that does not exist on that date — the hour
 * the spring transition skips.
 *
 * Each end is built from its own wall-clock time in the zone, never by adding
 * the span's length to its start: on the day the clocks go forward an hour of
 * wall clock is missing and on the day they go back an hour happens twice, so
 * a span's real duration is not its nominal one and only the endpoints know
 * which. For the same reason the end of the day is the next date's midnight
 * rather than 24 hours on.
 */
export function timeRangeOnDateToInstants(range, timeZone = UK_TIME_ZONE) {
    const start = zonedInstant(range.date, range.from, timeZone);
    // Stryker disable all -- dayjs happens to resolve "24:00:00" to the same
    // instant as the next day's midnight, so both branches agree and no test can
    // tell them apart. Kept explicit rather than relying on that: it is not part
    // of dayjs's contract, and strict parsing already rejects the same string.
    const end = range.until === END_OF_DAY
        ? startOfDayInZone(nextDate(range.date), timeZone)
        : zonedInstant(range.date, range.until, timeZone);
    // Stryker restore all
    // A time the spring transition skips moves later by the length of the jump,
    // so a span from inside that hour to its end fails to move forward.
    // Compared as milliseconds: on a zoned value `isAfter` goes through the
    // timezone plugin's `startOf`, which reads the wall clock in the host's zone.
    return end.valueOf() > start.valueOf()
        ? { start: start.toISOString(), end: end.toISOString() }
        : undefined;
}
/**
 * Instant spans as wall-clock spans, split at local midnight so none crosses a
 * day. Minutes are preserved exactly: an instant span is not constrained to
 * any grid, and must read as it was agreed rather than be silently moved.
 *
 * Unlike {@link timeRangeOnDateToInstants} this can fail, because an instant
 * arrives as a plain ISO string that nothing has yet shown to be one.
 */
export function instantsToTimeRangesOnDate(ranges, timeZone = UK_TIME_ZONE) {
    const dated = [];
    for (const range of ranges) {
        const rangeEnd = dayjs.utc(range.end).tz(timeZone);
        let cursor = dayjs.utc(range.start).tz(timeZone);
        if (!cursor.isValid() || !rangeEnd.isValid()) {
            return err(notAPairOfInstants(range));
        }
        while (cursor.isBefore(rangeEnd)) {
            const dayEnd = startOfDayInZone(IsoDateSchema.parse(cursor.add(1, "day").format(ISO_DATE_FORMAT)), timeZone);
            const reachesDayEnd = !rangeEnd.isBefore(dayEnd);
            // A wall-clock span holds whole minutes, so an instant agreed to the
            // second has no faithful representation. Reported rather than rounded:
            // moving an appointment silently is worse than refusing to show it.
            const from = TimeOfDaySchema.safeParse(cursor.format(TIME_FORMAT));
            const until = reachesDayEnd
                ? TimeOfDaySchema.safeParse(END_OF_DAY)
                : TimeOfDaySchema.safeParse(rangeEnd.format(TIME_FORMAT));
            if (!from.success || !until.success) {
                return err(buildInvalidError({
                    message: "Not an instant range",
                    issues: [
                        `"${range.start}" - "${range.end}" is not a whole number of minutes`,
                    ],
                }));
            }
            dated.push({
                date: IsoDateSchema.parse(cursor.format(ISO_DATE_FORMAT)),
                from: from.data,
                until: until.data,
            });
            cursor = dayEnd;
        }
    }
    return ok(dated);
}
/**
 * `range` widened to whole minutes: the start floored, the end raised. A
 * wall-clock span holds whole minutes, and widening keeps the error on the
 * side of not offering time that is already spoken for. Fails, with the same
 * error {@link instantsToTimeRangesOnDate} gives, when either end is not an
 * instant.
 */
export function widenToWholeMinutes(range) {
    const start = dayjs.utc(range.start);
    const end = dayjs.utc(range.end);
    if (!start.isValid() || !end.isValid()) {
        return err(notAPairOfInstants(range));
    }
    const wholeEnd = end.isSame(end.startOf("minute"))
        ? end
        : end.startOf("minute").add(1, "minute");
    return ok({
        start: start.startOf("minute").toISOString(),
        end: wholeEnd.toISOString(),
    });
}
function notAPairOfInstants(range) {
    return buildInvalidError({
        message: "Not an instant range",
        issues: [`"${range.start}" - "${range.end}" is not a pair of instants`],
    });
}
function nextDate(date) {
    return IsoDateSchema.parse(dayjs.utc(date, ISO_DATE_FORMAT).add(1, "day").format(ISO_DATE_FORMAT));
}
function zonedInstant(date, time, timeZone) {
    return instantOfWallClock({ wallClock: `${date} ${time}`, timeZone });
}
// Stryker disable next-line StringLiteral -- dayjs reads a bare date as
// midnight, so emptying the time changes nothing observable.
const START_OF_DAY = "00:00:00";
function startOfDayInZone(date, timeZone) {
    return zonedInstant(date, START_OF_DAY, timeZone);
}
//# sourceMappingURL=instant.js.map